import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Typography, Button, Divider, Spin } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import { convertNumberToWords } from "../../../lib/functions";
import ReportDom from "../../../components/common/ReportDom";
import { getEmployeePayableAmountApi } from "../../../apiservice/hrpayroll";
import commaNumber from "comma-number";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../Auth/authSelectors";

const format = commaNumber.bindWith(",", ".");
const { Title } = Typography;

const SalaryPaymentVoucher = () => {
  const { id: employeeId, month, year } = useParams();

  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef();
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency;

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Salary Payment Voucher - ${employee?.employee_name || employeeId}`,
  });

  useEffect(() => {
    if (employeeId && month && year) {
      fetchVoucher();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [employeeId, month, year]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const payload = {
        employeeId: employeeId,
        month,
        year,
        from: "voucher",
        fromDate: dayjs(`${month} ${year}`).startOf("month").toISOString(),
        toDate: dayjs(`${month} ${year}`).endOf("month").toISOString(),
      };
      const res = await getEmployeePayableAmountApi(payload);
   
      if (res?.data && res.data.length > 0) {
        setEmployee(res.data[0]);
      } else {
        setEmployee(null);
      }
    } catch (err) {
      console.error("Failed to load salary voucher", err);
      setEmployee(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate(-1)}>Back</Button>
      </div>
    );
  }

  // Build printable body similar to old UI
  const basicSalary = Number(employee.salary || 0);
  const payable = Number(employee.payable_salary || 0);
  const paid = Number(employee.paid_amount || 0);
  const currentDue = payable - paid;

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            Print Voucher
          </Button>
        </Col>
      </Row>

      {/* on-screen voucher preview */}
      <div style={{ background: "white", padding: 24, borderRadius: 8 }}>
        <Title level={4} style={{ textAlign: "center" }}>
          SALARY PAYMENT VOUCHER OF {month}, {year}
        </Title>

        <Row gutter={24} style={{ marginTop: 12 }}>
          <Col span={12}>
            <p><b>Employee Code :</b> {employee.employee_code}</p>
            <p><b>Employee Name :</b> {employee.employee_name}</p>
            <p><b>Contact No :</b> {employee.contact_no}</p>
            <p><b>Present Address :</b> {employee.present_address}</p>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <p><b>Designation :</b> {employee.designation_name}</p>
            <p><b>Department :</b> {employee.department_name}</p>
            <p><b>Voucher Generate Date :</b> {dayjs().format("DD MMM YYYY")}</p>
            <p><b>Basic Salary :</b> {format(basicSalary.toFixed(2))}</p>
          </Col>
        </Row>

        <Divider />

        {/* Attendance summary (you can extend rows as needed) */}
        <div>
          <h4>Attendance Summary :</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Day of The Month</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Precent Days</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Leave with Pay</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Leave without Pay</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>No. of Days Payable</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>1</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{employee.precent_days || 0}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{employee.leave_with_pay_days || 0}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{employee.leave_without_pay_days || 0}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{employee.payable_days || 0}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Divider />

        {/* Salary summary */}
        <div>
          <h4>Salary Summary :</h4>
          <p>
            Monthly Basic Salary : {format(basicSalary.toFixed(2))} , Target : {format(Number(employee.target || 0).toFixed(2))} , Commission % : {employee.commission_per || 0}
          </p>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Basic Salary</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Conveyance</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Overtime</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Others Commission</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Sales Commission</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Bonus</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>TA</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>DA</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>MA</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.salary || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.conveyance || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.overtime || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.commission || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.sales_commission || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.bonus || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.ta || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.da || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.ma || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.payable_salary || 0).toFixed(2))}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Divider />

        {/* Deductions */}
        <div>
          <h4>Deduction :</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Basic Salary (Paid)</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Advance Salary (Paid)</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Salary Deduction</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Others Payment</th>
                <th style={{ border: "1px solid #ddd", padding: 6 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.paid_basic_salary || employee.paid_amount || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.advance_paid || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.deduction_salary || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.others_payment || 0).toFixed(2))}</td>
                <td style={{ border: "1px solid #ddd", padding: 6 }}>{format(Number(employee.paid_amount || 0).toFixed(2))}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: 12, textAlign: "right" }}>
            <div><b>Salary Payable : </b> {format(payable.toFixed(2))}</div>
            <div><b>Paid :</b> {format(paid.toFixed(2))}</div>
            <div><b>Current Payable :</b> {format(currentDue.toFixed(2))}</div>
          </div>
        </div>

        <Divider />

        <p><b>In Word of Payable Amount : </b>{convertNumberToWords(currentDue, currency)}</p>
      </div>

      {/* Hidden printable voucher using ReportDom */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title={`SALARY PAYMENT VOUCHER OF ${month}, ${year}`}
          filters={{ employeeId, month, year }}
          renderTable={() => <SalaryPaymentVoucher authInfo={authInfo} printMode />}
        />
      </div>
    </div>
  );
};

export default SalaryPaymentVoucher;
